import {
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Tooltip,
} from '@chakra-ui/react';
import { FiMoreVertical } from 'react-icons/fi';
import { ResourceGridActions, ResourceGridContext } from '../../hooks/useResourceGridActions';
import { InsurancePlan } from '../../types/plans/types';
import { RESOURCE_GRID_COPY } from '../../utils/resourceGridCopy';

interface ResourceGridActionMenuProps {
  row: InsurancePlan;
  context: ResourceGridContext;
  actions: ResourceGridActions;
  planId?: string;
  marketId?: string;
}

export const ResourceGridActionMenu: React.FC<ResourceGridActionMenuProps> = ({
  row,
  context,
  actions,
  planId,
  marketId,
}) => {
  const providerName = `${row.provider.firstName || ''} ${row.provider.lastName || ''} ${row.provider.suffix || ''}`.trim();

  // Build menu items based on permissions and context
  const menuItems = [];

  // Edit Provider Credential Status (always first if permission allows)
  if (actions.canEditProviderCredentials) {
    menuItems.push(
      <Tooltip
        key="edit-provider"
        label={RESOURCE_GRID_COPY.tooltips.editProviderCredentialStatus}
        placement="left"
        hasArrow
      >
        <MenuItem
          onClick={() => actions.onEditProviderCredentialStatus({
            providerId: row.provider.id,
            planId,
            marketId,
            rowMeta: row,
          })}
          role="menuitem"
        >
          {RESOURCE_GRID_COPY.actions.editProviderCredentialStatus(
            row.provider.firstName || '',
            row.provider.lastName || '',
            row.provider.suffix || undefined
          )}
        </MenuItem>
      </Tooltip>
    );
  }

  // Bulk Edit Insurance Rows (always second if permission allows and not hidden)
  if (actions.canBulkEditInsurance && actions.showBulkEdit) {
    menuItems.push(
      <Tooltip
        key="bulk-edit"
        label={RESOURCE_GRID_COPY.tooltips.bulkEditInsuranceRows}
        placement="left"
        hasArrow
      >
        <MenuItem
          onClick={() => actions.onBulkEditInsuranceRows(context)}
          role="menuitem"
        >
          {RESOURCE_GRID_COPY.actions.bulkEditInsuranceRows}
        </MenuItem>
      </Tooltip>
    );
  }

  // View Plans (only show if not on plan detail page and permission allows)
  if (actions.showViewPlans && actions.canViewProviderPlans) {
    menuItems.push(
      <Tooltip
        key="view-plans"
        label={RESOURCE_GRID_COPY.tooltips.viewPlans}
        placement="left"
        hasArrow
      >
        <MenuItem
          onClick={() => actions.onViewPlans(row.provider.id, context)}
          role="menuitem"
        >
          {RESOURCE_GRID_COPY.actions.viewPlans}
        </MenuItem>
      </Tooltip>
    );
  }

  // Don't render menu if no items
  if (menuItems.length === 0) {
    return null;
  }

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={IconButton}
        aria-label={RESOURCE_GRID_COPY.a11y.rowActions(providerName)}
        icon={<FiMoreVertical />}
        variant="ghost"
        size="sm"
        minW="32px"
        minH="32px"
        data-testid="row-action-menu"
        _focus={{
          boxShadow: '0 0 0 2px var(--chakra-colors-blue-500)',
        }}
      />
      <MenuList role="menu">
        {menuItems}
      </MenuList>
    </Menu>
  );
};
